import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { type ViewFilters } from '@/types/schema/LiveAppView';
import { type ReportView, type ReportViewChart } from '@/types/schema/views/ReportView';
import { useViewContext } from '@/components/views/ViewContext';
import { useChartFiltersHelpers } from './useChartFiltersHelpers';

export interface ChartQueryParams {
  filters?: ViewFilters;
}

export type ViewQueryParam = keyof ChartQueryParams;

type ChartQueryParamsContextState = {
  params: ChartQueryParams;
  actions: {
    setChartQueryParam: (newParams: Partial<ChartQueryParams>) => void;
  };
};

type ChartQueryParamsContextProviderProps = {
  chart: ReportViewChart;
  chartIndex: number;
  children: React.ReactNode;
};

const ChartQueryParamsContext = createContext<ChartQueryParamsContextState | null>(null);

const getFiltersFromParams = (filtersParam: string): ViewFilters | undefined => {
  const parsedFilters: ViewFilters = JSON.parse(filtersParam);
  if (Array.isArray(parsedFilters)) {
    return undefined;
  }

  return parsedFilters.rules.length > 0
    ? { ...parsedFilters, rules: parsedFilters.rules }
    : undefined;
};

export function getExtractedChartParams(param: string) {
  // Extract the part of the param - {viewKey}_{chartIndex}_filters"
  // view_23_2_filters - view_23 (Report View) | 2 The index of the chart within the view | filters
  const parts = param.split('_');
  return parts.slice(3).join('_');
}

export function ChartQueryParamsContextProvider({
  chart,
  chartIndex,
  children
}: ChartQueryParamsContextProviderProps) {
  const { view } = useViewContext<ReportView>();
  const [searchParams, setSearchParams] = useSearchParams();

  const { getPresetFilters, getInitialMenuFilters } = useChartFiltersHelpers();

  const isInitialRender = useRef(true);

  const presetFilters = useMemo(() => getPresetFilters(chart), [getPresetFilters, chart]);
  const initialMenuFilters = useMemo(
    () => getInitialMenuFilters(chart),
    [getInitialMenuFilters, chart]
  );

  const chartQueryParams = useMemo(() => {
    const defaultParams: ChartQueryParams = {
      // We only set the preset and menu filters (if they exist) on the initial render
      ...(presetFilters &&
        !initialMenuFilters &&
        isInitialRender.current && { filters: presetFilters }),
      ...(initialMenuFilters &&
        !presetFilters &&
        isInitialRender.current && { filters: initialMenuFilters })
    };

    const viewParams = Object.fromEntries(searchParams.entries());

    if (!viewParams || Object.keys(viewParams).length === 0) {
      return defaultParams;
    }

    const extractedParams = Object.keys(viewParams).reduce((accumulatedParams, param) => {
      if (param.startsWith(`${view.key}_${chartIndex}`)) {
        const extractedParam = getExtractedChartParams(param);

        const value =
          extractedParam === 'filters'
            ? getFiltersFromParams(viewParams[param])
            : viewParams[param];

        const extractedFilters = extractedParam === 'filters' ? value : {};
        return {
          ...accumulatedParams,
          [extractedParam]: value,
          ...extractedFilters
        };
      }
      return accumulatedParams;
    }, defaultParams);

    return extractedParams;
  }, [presetFilters, initialMenuFilters, searchParams, view.key, chartIndex]);

  const setChartQueryParam = useCallback(
    (newParams: Partial<ChartQueryParams>) => {
      Object.entries(newParams).forEach(([key, value]) => {
        const prefixedKey = `${view.key}_${chartIndex}_${key as ViewQueryParam}` as const;
        if (value === undefined) {
          searchParams.delete(prefixedKey);
          return;
        }
        if (value) {
          if (key === 'filters') {
            searchParams.set(prefixedKey, JSON.stringify(value));
          }
        }
      });
      setSearchParams(searchParams, { replace: true });
    },
    [chartIndex, searchParams, setSearchParams, view.key]
  );

  const contextValue = useMemo(
    () => ({
      params: chartQueryParams,
      actions: {
        setChartQueryParam
      }
    }),
    [chartQueryParams, setChartQueryParam]
  );

  // This effect handles the setting of the preset and menu filters as search params in the URL when the component is first mounted
  useEffect(() => {
    const viewParams = Object.fromEntries(searchParams.entries());
    const hasPresetFilters = presetFilters && presetFilters.rules.length > 0;
    const hasMenuFilters = initialMenuFilters && initialMenuFilters.rules.length > 0;

    const hasEmptyViewFiltersParams = !viewParams[`${view.key}_${chartIndex}_filters`];

    if (isInitialRender.current && hasEmptyViewFiltersParams) {
      if (hasPresetFilters) {
        setChartQueryParam({ filters: presetFilters });
      } else if (hasMenuFilters) {
        setChartQueryParam({ filters: initialMenuFilters });
      }
    }

    isInitialRender.current = false;

    // Reset/cleanup the isInitialRender flag when the component is first unmounted. This is necessary for the component to work correctly in React's StrictMode.
    return () => {
      isInitialRender.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ChartQueryParamsContext.Provider value={contextValue}>
      {children}
    </ChartQueryParamsContext.Provider>
  );
}

export const useChartQueryParamsContext = () => {
  const context = useContext(ChartQueryParamsContext);

  if (!context) {
    throw new Error(
      'useChartQueryParamsContext must be used within a ChartQueryParamsContextProvider'
    );
  }

  return context;
};
